import { useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "../i18n/i18n";
import {
	CircleAlert,
	FileBarChart2,
	ArrowRight,
	RefreshCw,
} from "lucide-react";
import "../styles/analysis.css";

// Hooks
import { useAnalysisData } from "../hooks/useAnalysisData";
import { useAnalysisLogic } from "../hooks/useAnalysisLogic";

// Components
import StickyTitle from "../components/StickyTitle/StickyTitle";
import Loader from "../components/Loader/Loader";
import BarChart from "../components/Charts/BarChart";
import PieChart from "../components/Charts/PieChart";
import StackedBarChart from "../components/Charts/StackedBarChart";
import LineChart from "../components/Charts/LineChart";
import AreaChart from "../components/Charts/AreaChart";
import CategoryFilter from "../components/Filters/CategoryFilter";
import YearSelector from "../components/Filters/YearSelector";
import EmptyState from "../components/EmptyState/EmptyState";

const Analysis = () => {
	const { translate } = useTranslation();
	const navigate = useNavigate();

	useEffect(() => {
		// Add class to html on mount
		document.documentElement.classList.add("analysis-page-active");

		// Clean up on unmount
		return () => {
			document.documentElement.classList.remove("analysis-page-active");
		};
	}, []);

	// Hook to retrieve analysis data
	const { stats, loading, error, refetchStats } = useAnalysisData(translate);

	// Hook for analysis logic
	const {
		selectedYear,
		setSelectedYear,
		selectedCategories,
		allCategories,
		handleCategoryToggle,
		availableYears,
		expenseBarData,
		expenseAverages,
		expensePieData,
		overviewBarData,
		averagesData,
		incomeStackedBarData,
		incomePieData,
		yearlyCategoryTrendData,
		hasDataForYear,
		hasExpenseBarData,
		hasExpensePieData,
		hasOverviewBarData,
		hasIncomeStackedBarData,
		hasIncomePieData,
		monthlyBalanceData,
		yearlyBalanceData,
		hasMonthlyBalanceData,
		hasYearlyBalanceData,
		hasYearlyCategoryTrendData,
		hasAnyChartData,
	} = useAnalysisLogic(stats);

	// Navigation handler
	const handleNavigateToAdd = useCallback(() => {
		navigate("/add-transaction");
	}, [navigate]);

	// Retry handler
	const handleRetry = useCallback(() => {
		refetchStats();
	}, [refetchStats]);

	// Conditional rendering for loading and error states
	if (loading) {
		return (
			<div className="content">
				<StickyTitle title={translate("analysis.title")} />
				<div className="page-content">
					<Loader
						fullscreen={true}
						loadingTextKey="analysis.loading"
						theme="light"
						translate={translate}
					/>
				</div>
			</div>
		);
	}

	// Error state
	if (error) {
		return (
			<div className="content">
				<StickyTitle title={translate("analysis.title")} />
				<div className="page-content">
					<EmptyState
						icon={CircleAlert}
						title={translate("error.title")}
						description={`${translate("error.message")} ${error}`}
						variant="error"
						actionButton={{
							text: translate("error.retry"),
							onClick: handleRetry,
							icon: RefreshCw,
						}}
					/>
				</div>
			</div>
		);
	}

	// Check if there is data for the selected year
	if (!hasDataForYear) {
		return (
			<div className="content">
				<StickyTitle title={translate("analysis.title")} />
				<div className="page-content">
					<EmptyState
						icon={FileBarChart2}
						title={translate("analysis.empty.title")}
						description={translate("analysis.empty.description")}
						actionButton={{
							text: translate("form.add"),
							onClick: handleNavigateToAdd,
							icon: ArrowRight,
						}}
					/>
				</div>
			</div>
		);
	}

	// Main content
	return (
		<div className="content">
			<StickyTitle title={translate("analysis.title")} />

			{/* Global Filters */}
			<div className="global-filters glass-effect">
				<YearSelector
					availableYears={availableYears}
					selectedYear={selectedYear}
					onYearChange={setSelectedYear}
					translate={translate}
				/>
				<CategoryFilter
					categories={allCategories}
					selectedCategories={selectedCategories}
					onCategoryToggle={handleCategoryToggle}
					translate={translate}
				/>
			</div>

			<div className="page-content chart-sections">
				{/* Expense Bar Chart - Display only if data is available */}
				{hasExpenseBarData && (
					<div className="chart-wrapper">
						<h2 className="chart-title">
							{translate("analysis.monthlyExpensesByCategory")} - {selectedYear}
						</h2>
						<BarChart
							data={expenseBarData}
							averages={expenseAverages}
							translate={translate}
							title={translate("analysis.monthlyExpensesByCategory")}
							chartType="expenses"
						/>
					</div>
				)}

				{/* Expense Pie Chart - Display only if data is available */}
				{hasExpensePieData && (
					<div className="chart-wrapper">
						<h2 className="chart-title">
							{translate("analysis.expenseBreakdown")} - {selectedYear}
						</h2>
						<PieChart data={expensePieData} translate={translate} />
					</div>
				)}

				{/* Overview Bar Chart - Income, Expenses, Balance - Display only if data is available */}
				{hasOverviewBarData && (
					<div className="chart-wrapper">
						<h2 className="chart-title">
							{translate("analysis.monthlyOverview")} - {selectedYear}
						</h2>
						<BarChart
							data={overviewBarData}
							translate={translate}
							averages={averagesData}
							title={translate("analysis.monthlyOverview")}
							chartType="overview"
						/>
					</div>
				)}

				{/* Income Stacked Bar Chart - Display only if data is available */}
				{hasIncomeStackedBarData && (
					<div className="chart-wrapper">
						<h2 className="chart-title">
							{translate("analysis.monthlyIncome")} - {selectedYear}
						</h2>
						<StackedBarChart
							data={incomeStackedBarData}
							translate={translate}
							averages={averagesData.income}
							title={translate("analysis.monthlyIncome")}
						/>
					</div>
				)}

				{/* Income Pie Chart by Subcategory - Display only if data is available */}
				{hasIncomePieData && (
					<div className="chart-wrapper">
						<h2 className="chart-title">
							{translate("analysis.incomeBySubcategory")} - {selectedYear}
						</h2>
						<PieChart data={incomePieData} translate={translate} />
					</div>
				)}

				{/* Yearly evolution by category Line Chart - Display only if data is available */}
				{hasYearlyCategoryTrendData && (
					<div className="chart-wrapper">
						<h2 className="chart-title">
							{translate("analysis.yearlyEvolutionByCategory")}
						</h2>
						<LineChart
							data={yearlyCategoryTrendData}
							translate={translate}
							averages={stats?.averages}
							displayMetrics={stats?.displayMetrics}
							yearlyGrowthRates={stats?.yearlyGrowthRates || {}}
						/>
					</div>
				)}

				{/* Monthly Balance Area Chart - Display only if data is available */}
				{hasMonthlyBalanceData && (
					<div className="chart-wrapper">
						<h2 className="chart-title">
							{translate("analysis.monthlyBalance")} - {selectedYear}
						</h2>
						<AreaChart
							data={monthlyBalanceData}
							translate={translate}
							title={translate("analysis.monthlyBalance")}
							isMonthly={true}
						/>
					</div>
				)}

				{/* Yearly Balance Area Chart - Display only if data is available */}
				{hasYearlyBalanceData && (
					<div className="chart-wrapper">
						<h2 className="chart-title">
							{translate("analysis.yearlyBalance")}
						</h2>
						<AreaChart
							data={yearlyBalanceData}
							translate={translate}
							title={translate("analysis.yearlyBalance")}
							averages={stats?.averages}
							displayMetrics={stats?.displayMetrics}
						/>
					</div>
				)}

				{/* Message if no chart has data to display */}
				{!hasAnyChartData && (
					<EmptyState
						icon={FileBarChart2}
						title={translate("analysis.noChartsData.title")}
						description={translate("analysis.noChartsData.description")}
						actionButton={{
							text: translate("form.add"),
							onClick: handleNavigateToAdd,
							icon: ArrowRight,
						}}
					/>
				)}
			</div>
		</div>
	);
};

export default Analysis;
